/**
 * React binding for `door-overrides.ts`.
 *
 * Re-reads on three things: the store's own change event (a declare or
 * a revoke, from anywhere in this tab), the active site changing, and
 * -- ONLY while at least one record exists -- a 1 Hz tick, so a
 * countdown runs and a lapsed override drops its chip in view rather
 * than at the next unrelated render.
 *
 * The tick is conditional on purpose. Every door row on the Devices
 * screen reads this, so an unconditional 1 Hz interval would re-render
 * the whole table forever at a site that has never declared an
 * override -- which is every site, almost all of the time.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSafeSiteId } from './useActiveSite';
import {
  activeDoorOverrides,
  subscribeDoorOverrides,
  type DoorOverrideRecord,
} from './door-overrides';

export function useDoorOverrides() {
  const site = useSafeSiteId();
  const [now, setNow] = useState(() => Date.now());
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!site) return;
    return subscribeDoorOverrides(site, bump);
  }, [site, bump]);

  const records = useMemo(
    () => (site ? activeDoorOverrides(site, now) : []),
    // `version` is a deliberate dependency: it is the store's change
    // signal, and the memo has to recompute on it even though it is
    // not read inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [site, now, version],
  );

  const hasAny = records.length > 0;
  useEffect(() => {
    if (!hasAny) return;
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [hasAny]);

  const byDoor = useMemo(() => {
    const map = new Map<string, DoorOverrideRecord>();
    for (const r of records) map.set(r.door, r);
    return map;
  }, [records]);

  return { records, byDoor, now };
}
